import { getRoofFaces } from "building-common/roofs/roofQuery";
import { type GeneratedRoof, type GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { VALLEY_RAIL_TOLERANCE, RIDGE_RAIL_TOLERANCE } from "building-types/roofs/roofConstants";
import type { Face } from "geom/brep";
import type { ClipRailContext } from "geom/bspClipping";
import { clipRail3, clippingNodeNew } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { joinPolyLine3Rails } from "geom/join";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ByTwoPoints, polyLine3EdgeCount, polyLine3EdgeEnd, polyLine3EdgeStart } from "geom/polyLine3";

// used to get eave rails that might have a type of "RAKE" or "EAVE"
function getHorizontalSegmentsFromRail(rail: PolyLine3): PolyLine3[] {
  const segments: PolyLine3[] = [];

  for (let i = 0, l = rail.length - 3; i < l; i += 3) {
    const j = i + 3;
    if (Math.abs(rail[i + 2] - rail[j + 2]) < DIST_EPSILON) {
      segments.push(polyLine3ByTwoPoints(rail[i], rail[i + 1], rail[i + 2], rail[j], rail[j + 1], rail[j + 2]));
    }
  }

  return segments;
}

export function getRoofSystemFaces(
  roof: GeneratedRoofSystem,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): Face[] {
  const faces: Face[] = [];

  const { roofs: subRoofs } = roof;
  const allSubRoofs = Object.values(subRoofs);

  for (const subroof of allSubRoofs) {
    if (filter && !filter(subroof)) {
      continue;
    }
    faces.push(...getRoofFaces(subroof));
  }

  return faces;
}

export function getRoofEaves(
  roof: GeneratedRoofSystem,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): PolyLine3[] {
  const finalEaves: PolyLine3[] = [];

  const { roofs: subRoofs, clipContext } = roof;
  const allSubRoofs = Object.values(subRoofs);

  const eaveRails: PolyLine3[] = [];
  const joinedRails: PolyLine3[] = [];

  for (let i = 0, l = allSubRoofs.length; i < l; i++) {
    const subRoof = allSubRoofs[i];
    if (filter && !filter(subRoof)) {
      continue;
    }
    const { rails, roofPerimeterRails } = subRoof;
    roofPerimeterRails.forEach((x) => {
      eaveRails.push(...getHorizontalSegmentsFromRail(x.rail));
    });

    // account for eave returns
    eaveRails.push(...rails.returnEaveRails);
  }

  joinedRails.push(
    ...joinPolyLine3Rails({
      segments: eaveRails,
      searchBoxTolerance: VALLEY_RAIL_TOLERANCE,
      simplify: true,
      allowRevert: false,
    }),
  );
  const railOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(clipContext.insideBSP)],
  };

  joinedRails.forEach((inputRail) =>
    clipRail3({
      rail: inputRail,
      options: railOptions,
      clippedRails: finalEaves,
    }),
  );

  return finalEaves;
}

export function getRoofRakes(
  roof: GeneratedRoofSystem,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): PolyLine3[] {
  const finalRails: PolyLine3[] = [];

  const { roofs: subRoofs, clipContext } = roof;
  const allSubRoofs = Object.values(subRoofs);

  const rakeRails: PolyLine3[] = [];
  for (let i = 0, l = allSubRoofs.length; i < l; i++) {
    const subRoof = allSubRoofs[i];
    if (filter && !filter(subRoof)) {
      continue;
    }
    const { roofPerimeterRails } = subRoof;

    roofPerimeterRails.forEach(({ rail }) => {
      const edgeCount = polyLine3EdgeCount(rail);
      for (let j = 0; j < edgeCount; j++) {
        const [startX, startY, startZ] = polyLine3EdgeStart(rail, j);
        const [endX, endY, endZ] = polyLine3EdgeEnd(rail, j);

        const isHorizontal = Math.abs(startZ - endZ) < DIST_EPSILON;
        if (!isHorizontal) {
          rakeRails.push(polyLine3ByTwoPoints(startX, startY, startZ, endX, endY, endZ));
        }
      }
    });
  }

  const joinedRails: PolyLine3[] = [];

  joinedRails.push(
    ...joinPolyLine3Rails({
      segments: rakeRails,
      searchBoxTolerance: RIDGE_RAIL_TOLERANCE,
      simplify: true,
      allowRevert: false,
    }),
  );

  const railOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(clipContext.insideBSP)],
  };

  joinedRails.forEach((inputRail) =>
    clipRail3({
      rail: inputRail,
      options: railOptions,
      clippedRails: finalRails,
    }),
  );

  return finalRails;
}

export function getRoofRidgeVents(
  roof: GeneratedRoofSystem,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): PolyLine3[] {
  const ridgeRailVents: PolyLine3[] = [];

  const { roofs: subRoofs } = roof;
  const allSubRoofs = Object.values(subRoofs);

  for (let i = 0, l = allSubRoofs.length; i < l; i++) {
    const subRoof = allSubRoofs[i];
    if (filter && !filter(subRoof)) {
      continue;
    }
    subRoof.rails.ridgeRails.forEach((x) => ridgeRailVents.push(...x.ridgeVentRails));
  }

  return ridgeRailVents;
}
